import { createContext, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { userApi } from "@/api/account-management/userApi";
import { HttpError } from "@/api/common";
import { type CurrentUser } from '@/models/account-management/User';
import { useStytchMemberSession, useStytchB2BClient } from '@stytch/react/b2b';

type CurrentUserContextType = {
    user?: CurrentUser;
    isLoading: boolean;
    // The HTTP status of a definitive rejection, kept so the sign-in screen can say WHY the account was
    // turned away. It outlives the revoked session on purpose: by the time Login renders, the session is
    // already gone and the query that produced the error is disabled, so nothing else still knows.
    refusedStatus?: number;
    // True when the account could not be checked at all — a 500, a dropped connection. NOT a refusal: the
    // account may be perfectly fine. Distinguished from refusedStatus because the sign-in screen has to say
    // something different ("try again") and must not accuse a valid user of being disabled.
    verificationFailed: boolean;
    // Called when a fresh sign-in is attempted, so the previous refusal stops being shown.
    clearRefusal: () => void;
};

const CurrentUserContext = createContext<CurrentUserContextType | undefined>(undefined);

export function useCurrentUser() {
    const context = useContext(CurrentUserContext);

    if (!context) {
        throw new Error("useCurrentUser must be used within CurrentUserProvider");
    }

    const hasPermission = (permission: string) => {
        const split = permission.split(":");
        const requiredAccess = getPermissionNumber(split[0]);

        if (!requiredAccess) {
            console.log(`Invalid permission ${permission} - unknown access type ${split[0]}`);
            return false;
        }

        const permissionId = split[1];        
        const perm = context.user?.permissions?.find(p => p.id === permissionId);

        if (!perm) {
            console.log(`Permission ${permission} not found for user ${context.user?.email}`);
            return false;
        }

        return (perm.access & requiredAccess) === requiredAccess;
    };

    const permissionMap: Record<string, number> = {
        read: 1,
        create: 2,
        update: 4,        
        delete: 8,
        write: 2 | 4
    };
    const getPermissionNumber = (permission: string) => permissionMap[permission];

    return {
        ...context,
        hasPermission
    };
}

// A live Stytch session is not proof of access. An account can be deleted or disabled on our side while the
// Stytch member can still authenticate, and without this the portal would render a signed-in shell for someone
// who no longer has an account — no permissions, every screen empty, but apparently logged in.
const rejectedByApi = (error: unknown) =>
    error instanceof HttpError && (error.status === 401 || error.status === 403 || error.status === 404);

const CurrentUserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { session, isInitialized } = useStytchMemberSession();
    const stytch = useStytchB2BClient();
    const qc = useQueryClient();
    const [refusedStatus, setRefusedStatus] = useState<number | undefined>();
    const isLoggedIn = isInitialized && session !== null;
    const { data, isLoading, error } = useQuery({
        queryKey: ["currentUser"],
        queryFn: userApi.getCurrentUser,
        enabled: isLoggedIn,
        // Retrying a definitive rejection just delays the sign-out; anything else is worth retrying.
        retry: (failureCount, err) => !rejectedByApi(err) && failureCount < 3,
    });

    useEffect(() => {
        // Only a definitive rejection signs the user out. A 500 or a dropped connection must not eject
        // someone whose account is perfectly valid.
        if (isLoggedIn && rejectedByApi(error)) {
            setRefusedStatus((error as HttpError).status);
            void stytch.session.revoke();
        }
    }, [isLoggedIn, error, stytch]);

    // Dropping the cached failure matters as much as clearing the message: react-query would otherwise hand
    // the next attempt the same error object, the effect above would not see a changed dependency, and a
    // second sign-in by the same disabled user would leave a live session that nothing revokes.
    const clearRefusal = () => {
        setRefusedStatus(undefined);
        qc.removeQueries({ queryKey: ["currentUser"] });
    };

    return (
        <CurrentUserContext.Provider
            value={{
                user: data,
                isLoading,
                refusedStatus,
                verificationFailed: !!error && !rejectedByApi(error),
                clearRefusal,
            }}
        >
            {children}
        </CurrentUserContext.Provider>
    );
};

export default CurrentUserProvider;